// Realistic metallic finish styles with CSS gradients
import { supabase } from "@/integrations/supabase/client";

export interface FinishStyle {
  background: string;
  boxShadow: string;
  image?: string; // Optional image path for texture-based swatches (e.g., Deante)
}

// ========== DB Override Cache ==========
interface DbFinishOverride {
  finish_name: string;
  gradient_css: string | null;
  solid_color: string | null;
  box_shadow: string | null;
  image_url: string | null;
  aliases: string[];
}

let dbOverrides: DbFinishOverride[] | null = null;
let dbFetchPromise: Promise<void> | null = null;
let lastFetchTime = 0;
const CACHE_TTL = 60_000; // 1 minute

async function ensureDbOverrides(): Promise<DbFinishOverride[]> {
  if (dbOverrides && Date.now() - lastFetchTime < CACHE_TTL) return dbOverrides;
  if (dbFetchPromise) { await dbFetchPromise; return dbOverrides || []; }

  dbFetchPromise = (async () => {
    try {
      const { data } = await (supabase.from as any)('finish_styles').select('finish_name, gradient_css, solid_color, box_shadow, image_url, aliases');
      dbOverrides = (data as DbFinishOverride[]) || [];
      lastFetchTime = Date.now();
    } catch { dbOverrides = dbOverrides || []; }
    dbFetchPromise = null;
  })();
  await dbFetchPromise;
  return dbOverrides || [];
}

// Trigger background fetch on module load
ensureDbOverrides();

// Subscribe to realtime changes
supabase
  .channel('finish-styles-sync')
  .on('postgres_changes', { event: '*', schema: 'public', table: 'finish_styles' }, () => {
    dbOverrides = null; // Invalidate cache
    ensureDbOverrides();
  })
  .subscribe();

function findDbOverride(finish: string): DbFinishOverride | null {
  if (!dbOverrides?.length) return null;
  const lower = finish.toLowerCase().trim();
  const upper = finish.toUpperCase().trim();

  // Direct name match
  for (const o of dbOverrides) {
    if (o.finish_name === upper) return o;
  }
  // Alias match
  for (const o of dbOverrides) {
    if (o.aliases?.some(a => a === lower)) return o;
  }
  // Partial match
  for (const o of dbOverrides) {
    if (upper.includes(o.finish_name) || o.finish_name.includes(upper)) return o;
    if (o.aliases?.some(a => lower.includes(a) || a.includes(lower))) return o;
  }
  return null;
}

function dbToFinishStyle(o: DbFinishOverride): FinishStyle {
  return {
    background: o.gradient_css || o.solid_color || '#666',
    boxShadow: o.box_shadow || 'inset 0 1px 2px rgba(255,255,255,0.15)',
    image: o.image_url || undefined,
  };
}

function dbToSolidColor(o: DbFinishOverride): string {
  return o.solid_color || '#666666';
}

// Deante finish image mappings (use lowercase keys for matching)
import deanteTitanium from '@/assets/finishes/deante-titanium.webp';
import deanteNero from '@/assets/finishes/deante-nero.webp';
import deanteZloty from '@/assets/finishes/deante-zloty.webp';
import deanteChrom from '@/assets/finishes/deante-chrom.webp';
import deanteStalSzczotkowana from '@/assets/finishes/deante-stal-szczotkowana.webp';
import deanteZlotoSzczotkowane from '@/assets/finishes/deante-zloto-szczotkowane.webp';
import deanteBrazSzczotkowany from '@/assets/finishes/deante-braz-szczotkowany.webp';
import deanteMosiadz from '@/assets/finishes/deante-mosiadz.webp';
import deanteBianco from '@/assets/finishes/deante-bianco.webp';
import deanteTitaniumMatt from '@/assets/finishes/deante-titanium-matt.webp';
import deanteSand from '@/assets/finishes/deante-sand.webp';
import deanteGrey from '@/assets/finishes/deante-grey.webp';
import deanteBrushedSatin from '@/assets/finishes/deante-brushed-satin.webp';
import deanteGraphite from '@/assets/finishes/deante-graphite.webp';
import deanteAnthracite from '@/assets/finishes/deante-anthracite.webp';
import deanteGoldMatt from '@/assets/finishes/deante-gold-matt.webp';
import deanteCashmere from '@/assets/finishes/deante-cashmere.webp';
import deanteBrownMat from '@/assets/finishes/deante-brown-mat.webp';
import deanteWhiteGloss from '@/assets/finishes/deante-white-gloss.webp';
import deanteAlabaster from '@/assets/finishes/deante-alabaster.webp';
import deanteBrown from '@/assets/finishes/deante-brown.webp';
import deanteGrafitMetalik from '@/assets/finishes/deante-grafit-metalik.webp';
import deanteBeige from '@/assets/finishes/deante-beige.webp';

// Map Deante finish names to images
export const DEANTE_FINISH_IMAGES: Record<string, string> = {
  'titanium': deanteTitanium,
  'nero': deanteNero,
  'zloty': deanteZloty,
  // 'gold': deanteZloty, // Removed - use clean gradient instead
  // 'chrom': deanteChrom,
  // 'chrome': deanteChrom,
  'stal szczotkowana': deanteStalSzczotkowana,
  'brushed steel': deanteStalSzczotkowana,
  // 'zloto szczotkowane': deanteZlotoSzczotkowane, // Removed as it looks too "dirty" with gray stripes
  // 'brushed gold': deanteZlotoSzczotkowane,
  // 'brushed glod': deanteZlotoSzczotkowane,
  // 'braz szczotkowany': deanteBrazSzczotkowany,
  'brushed bronze': deanteBrownMat,
  'warm sunset': deanteBrownMat,
  'mosiadz': deanteMosiadz,
  'brass': deanteMosiadz,
  'bianco': deanteBianco,
  'white': deanteBianco,
  'black': deanteNero,
  'czarny': deanteNero,
  'biały': deanteBianco,
  // New finishes
  'titanium matt': deanteTitaniumMatt,
  'matt titanium': deanteTitaniumMatt,
  'sand': deanteSand,
  'piasek': deanteSand,
  'beige': deanteBeige,
  'grey': deanteGrey,
  'gray': deanteGrey,
  'szary': deanteGrey,
  'gray matt': deanteGrey,
  'grey matt': deanteGrey,
  'grey metallic': deanteGrey,
  'brushed satin': deanteBrushedSatin,
  'satin': deanteBrushedSatin,
  'steel satin': deanteBrushedSatin,
  'matt steel': deanteBrushedSatin,
  'polished steel': deanteBrushedSatin,
  'stainless steel': deanteBrushedSatin,
  'steel': deanteBrushedSatin,
  'graphite': deanteGraphite,
  'matt graphite': deanteGraphite,
  'grafit': deanteGraphite,
  'graphite metalic': deanteGraphite,
  'graphite metallic': deanteGraphite,
  // 'anthracite': deanteAnthracite,
  // 'anthracite metallic': deanteAnthracite,
  // 'antracyt': deanteAnthracite,
  // Gold Matt
  'gold matt': deanteGoldMatt,
  'matt gold': deanteGoldMatt,
  'złoto matowe': deanteGoldMatt,
  'zloto matowe': deanteGoldMatt,
  // Cashmere
  'cashmere': deanteCashmere,
  'kaszmirowy': deanteCashmere,
  // Brown Mat
  'brown mat': deanteBrownMat,
  'brown matt': deanteBrownMat,
  'brązowy mat': deanteBrownMat,
  'brazowy mat': deanteBrownMat,
  'brown': deanteBrown,
  // White Gloss
  'white gloss': deanteWhiteGloss,
  'biały połysk': deanteWhiteGloss,
  'bialy polysk': deanteWhiteGloss,
  'gloss white': deanteWhiteGloss,
  // Alabaster
  'alabaster': deanteAlabaster,
  'alabastrowy': deanteAlabaster,
  // Additional Polish/variant mappings for products with finish options
  'beż': deanteBeige,
  'bezowy': deanteBeige,
  'grafit metalik': deanteGrafitMetalik,
  'stal': deanteBrushedSatin,
  'brązowy': deanteBrown,
  'brazowy': deanteBrown,
  'transparentny': deanteBianco, // Use white/bianco as fallback for transparent
  'transparent': deanteBianco,
  'grafika': deanteGraphite,
};

// Get Deante finish image if available
export const getDeanteFinishImage = (finish: string): string | null => {
  const lowerFinish = finish.toLowerCase().trim();
  
  // Check DB overrides first (if has image_url)
  const dbMatch = findDbOverride(finish);
  if (dbMatch?.image_url) return dbMatch.image_url;
  
  // Direct match
  if (DEANTE_FINISH_IMAGES[lowerFinish]) {
    return DEANTE_FINISH_IMAGES[lowerFinish];
  }
  
  // Partial match
  for (const key of Object.keys(DEANTE_FINISH_IMAGES)) {
    if (lowerFinish.includes(key) || key.includes(lowerFinish)) {
      return DEANTE_FINISH_IMAGES[key];
    }
  }
  
  return null;
};

const finishStyles: Record<string, FinishStyle> = {
  'BLACK': {
    background: 'linear-gradient(135deg, #3a3a3a 0%, #0d0d0d 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.08)',
  },
  'SMOKED BRONZE': {
    background: 'linear-gradient(135deg, #3d3730 0%, #1a1612 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.05)',
  },
  'BRONZE': {
    background: 'linear-gradient(135deg, #8b5a28 0%, #3e2209 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,180,80,0.12)',
  },
  'BRASS': {
    background: 'linear-gradient(135deg, #f0d060 0%, #b08820 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,245,180,0.3)',
  },
  'STEEL': {
    background: 'linear-gradient(135deg, #b0b8be 0%, #5a6268 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.25)',
  },
  'CHROME': {
    background: 'linear-gradient(135deg, #ffffff 0%, #cccccc 100%)',
    boxShadow: 'inset 0 1px 3px rgba(255,255,255,0.8)',
  },
  'NICKEL': {
    background: 'linear-gradient(135deg, #a0a0a0 0%, #585858 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2)',
  },
  'GOLD': {
    background: 'linear-gradient(135deg, #ffe566 0%, #c49800 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,180,0.4)',
  },
  'BRUSHED GOLD': {
    background: 'linear-gradient(135deg, #e8c96a 0%, #9a7010 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,240,150,0.3)',
  },
  'GOLD MATT': {
    background: 'linear-gradient(135deg, #d4af37 0%, #8a6800 100%)',
    boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.1)',
  },
  'WHITE': {
    background: 'linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.9)',
  },
  'COPPER': {
    background: 'linear-gradient(135deg, #d4865a 0%, #7a2e00 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,160,100,0.25)',
  },
  'BURNT STEEL': {
    background: 'linear-gradient(135deg, #8090cc 0%, #3a2860 100%)',
    boxShadow: 'inset 0 1px 2px rgba(180,190,255,0.15)',
  },
  'GUN METAL': {
    background: 'linear-gradient(135deg, #4a5258 0%, #1e2529 100%)',
    boxShadow: 'inset 0 1px 2px rgba(150,160,170,0.12)',
  },
  'ANTHRACITE METALLIC': {
    background: 'linear-gradient(135deg, #2e2e2e 0%, #080808 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.04)',
  },
  'WARM SUNSET': {
    background: 'radial-gradient(ellipse at 40% 40%, #e8a878 0%, #c27a4a 35%, #b06a3a 60%, #9a5a30 100%)',
    boxShadow: 'inset 0 1px 3px rgba(255,200,150,0.3)',
  },
  'WAVE BLUE': {
    background: 'linear-gradient(135deg, #2a6b6b 0%, #1a4a4a 50%, #0f3535 100%)',
    boxShadow: 'inset 0 1px 2px rgba(100,200,200,0.15)',
  },
  'SMOKE GREY': {
    background: 'linear-gradient(135deg, #5a5a5a 0%, #2e2e2e 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.1)',
  },
  'AMBER COPPER': {
    background: 'linear-gradient(135deg, #d4865a 0%, #a05a2a 50%, #7a3a10 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,160,100,0.25)',
  },
  // TRES finishes
  'BLACK BRONZE': {
    background: 'radial-gradient(ellipse at 35% 35%, #8a7a50 0%, #5a4a2a 40%, #3a3018 70%, #2a2010 100%)',
    boxShadow: 'inset 0 1px 2px rgba(180,160,100,0.15)',
  },
  'METALLIC BLACK': {
    background: 'radial-gradient(ellipse at 35% 35%, #4a4a4a 0%, #1a1a1a 50%, #0a0a0a 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.06)',
  },
  'VOLCANIC': {
    background: 'radial-gradient(ellipse at 35% 35%, #c89080 0%, #a06858 40%, #8a5548 70%, #704038 100%)',
    boxShadow: 'inset 0 1px 2px rgba(220,160,140,0.2)',
  },
  'GOLDEN CAVA': {
    background: 'radial-gradient(ellipse at 35% 35%, #d4b870 0%, #b89840 40%, #a08530 70%, #8a7020 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,220,120,0.25)',
  },
  '24K GOLD': {
    background: 'radial-gradient(ellipse at 30% 30%, #fff4a0 0%, #ffd700 30%, #e8b800 60%, #c89800 100%)',
    boxShadow: 'inset 0 1px 3px rgba(255,255,200,0.5)',
  },
  '24K MATTE GOLD': {
    background: 'radial-gradient(ellipse at 35% 35%, #e8c860 0%, #d4a830 40%, #c09020 70%, #a87a10 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,220,100,0.2)',
  },
  '24K MATTE ROSE GOLD': {
    background: 'radial-gradient(ellipse at 35% 35%, #f0c0a8 0%, #dca888 40%, #c89878 70%, #b48868 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,200,180,0.25)',
  },
  'WALNUT LAMINATE': {
    background: 'linear-gradient(135deg, #8b6b4a 0%, #6b4c33 40%, #5a3d28 70%, #7a5c3e 100%)',
    boxShadow: 'inset 0 1px 2px rgba(180,140,100,0.2)',
  },
  'OAK LAMINATE': {
    background: 'linear-gradient(135deg, #c8b48a 0%, #b5a078 40%, #a89068 70%, #bca880 100%)',
    boxShadow: 'inset 0 1px 2px rgba(210,190,150,0.25)',
  },
  // Deante & additional finishes
  'NERO': {
    background: 'linear-gradient(135deg, #3a3a3a 0%, #0d0d0d 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.08)',
  },
  'TITANIUM': {
    background: 'linear-gradient(135deg, #7a7a7a 0%, #4a4a4a 50%, #5a5a5a 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.15)',
  },
  'TITANIUM MATT': {
    background: 'linear-gradient(135deg, #6a6a6a 0%, #3e3e3e 50%, #4a4a4a 100%)',
    boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.08)',
  },
  'GRAPHITE': {
    background: 'linear-gradient(135deg, #5a5a5a 0%, #2d2d2d 50%, #3d3d3d 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.08)',
  },
  'MATT GRAPHITE': {
    background: 'linear-gradient(135deg, #4a4a4a 0%, #2a2a2a 50%, #383838 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.06)',
  },
  'GRAFIT METALIK': {
    background: 'linear-gradient(135deg, #5a5a5a 0%, #2d2d2d 50%, #3d3d3d 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.1)',
  },
  'ANTHRACITE': {
    background: 'linear-gradient(135deg, #3a3a3a 0%, #1a1a1a 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.05)',
  },
  'CHROM': {
    background: 'linear-gradient(135deg, #f0f0f0 0%, #c0c0c0 50%, #d8d8d8 100%)',
    boxShadow: 'inset 0 1px 3px rgba(255,255,255,0.8)',
  },
  'BRUSHED BRONZE': {
    background: 'linear-gradient(135deg, #6b4a28 0%, #3e2a14 50%, #5a3c20 100%)',
    boxShadow: 'inset 0 1px 2px rgba(180,130,80,0.15)',
  },
  'BRUSHED NICKEL': {
    background: 'linear-gradient(135deg, #a8a898 0%, #7a7a6a 50%, #8a8a7a 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2)',
  },
  'BRUSHED SATIN': {
    background: 'linear-gradient(135deg, #c0c0b8 0%, #a0a098 50%, #b0b0a8 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.25)',
  },
  'BRUSHED STEEL': {
    background: 'linear-gradient(135deg, #a8b0b8 0%, #7a8890 50%, #8a9298 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2)',
  },
  'WHITE GLOSS': {
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 50%, #ffffff 100%)',
    boxShadow: 'inset 0 1px 3px rgba(255,255,255,0.9)',
  },
  'WHITE MATT': {
    background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #f0f0f0 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.5)',
  },
  'GREY': {
    background: 'linear-gradient(135deg, #8a8a8a 0%, #5a5a5a 50%, #6a6a6a 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.12)',
  },
  'BEIGE': {
    background: 'linear-gradient(135deg, #f5f0dc 0%, #e8dfc8 50%, #f0ebd4 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,250,220,0.4)',
  },
  'SAND': {
    background: 'linear-gradient(135deg, #e0d4b8 0%, #c8bca0 50%, #d4c4a8 100%)',
    boxShadow: 'inset 0 1px 2px rgba(240,220,180,0.3)',
  },
  'ALABASTER': {
    background: 'linear-gradient(135deg, #f0e8dc 0%, #e0d4c4 50%, #e8dfd0 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,245,230,0.4)',
  },
  'CASHMERE': {
    background: 'linear-gradient(135deg, #e0d8c8 0%, #c8bca8 50%, #d4c8b8 100%)',
    boxShadow: 'inset 0 1px 2px rgba(240,225,200,0.3)',
  },
  'BROWN': {
    background: 'linear-gradient(135deg, #8b5c35 0%, #5a3818 50%, #6b4423 100%)',
    boxShadow: 'inset 0 1px 2px rgba(180,120,60,0.15)',
  },
  'BROWN MATT': {
    background: 'linear-gradient(135deg, #7a4c28 0%, #4a2c12 50%, #5a3a1f 100%)',
    boxShadow: 'inset 0 1px 1px rgba(160,100,50,0.1)',
  },
  'BIANCO': {
    background: 'linear-gradient(135deg, #fdfdf5 0%, #f0f0e8 50%, #f8f8f0 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,250,0.6)',
  },
  'BLACK GLASS': {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #050505 50%, #0a0a0a 100%)',
    boxShadow: 'inset 0 1px 3px rgba(255,255,255,0.06)',
  },
  'INOX': {
    background: 'linear-gradient(135deg, #b8b8b8 0%, #8a8a8a 50%, #9a9a9a 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2)',
  },
  'STAINLESS STEEL': {
    background: 'linear-gradient(135deg, #b8b8b8 0%, #8a8a8a 50%, #9a9a9a 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2)',
  },
  'CHOCOLATE': {
    background: 'linear-gradient(135deg, #5a3520 0%, #2d1a0c 50%, #3d2314 100%)',
    boxShadow: 'inset 0 1px 2px rgba(120,80,40,0.15)',
  },
};

// Simple solid colors for split swatches
const finishSolidColors: Record<string, string> = {
  // Buster+Punch finishes
  'BLACK': '#1a1a1a',
  'SMOKED BRONZE': '#4a433a',
  'BRONZE': '#a0652a',
  'BRASS': '#d4a84b',
  'STEEL': '#71797E',
  'CHROME': '#c0c0c0',
  'NICKEL': '#727472',
  'GOLD': '#ffd700',
  'WHITE': '#f5f5f5',
  'COPPER': '#b87333',
  'BURNT STEEL': '#5a4a65',
  'GUN METAL': '#2a3439',
  // Deante finishes
  'NERO': '#1a1a1a',
  'TITANIUM': '#5a5a5a',
  'TITANIUM MATT': '#4a4a4a',
  'GRAPHITE': '#3d3d3d',
  'MATT GRAPHITE': '#383838',
  'GRAFIT METALIK': '#3d3d3d',
  'ANTHRACITE': '#1a1a1a',
  'ANTHRACITE METALLIC': '#0f0f0f',
  'CHROM': '#c0c0c0',
  'BRUSHED GOLD': '#dfc07f',
  'BRUSHED BRONZE': '#3e2a14',
  'BRUSHED NICKEL': '#8a8a7a',
  'BRUSHED SATIN': '#a8a8a0',
  'BRUSHED STEEL': '#8a9298',
  'GOLD MATT': '#c9a227',
  'WHITE GLOSS': '#ffffff',
  'WHITE MATT': '#f0f0f0',
  'GREY': '#6a6a6a',
  'GRAY': '#6a6a6a',
  'BEIGE': '#f5f5dc',
  'SAND': '#d4c4a8',
  'ALABASTER': '#e8dfd0',
  'CASHMERE': '#d4c8b8',
  'BROWN': '#6b4423',
  'BROWN MATT': '#5a3a1f',
  'BIANCO': '#f8f8f0',
  'BLACK GLASS': '#0a0a0a',
  'INOX': '#9a9a9a',
  'STAINLESS STEEL': '#9a9a9a',
  'CHOCOLATE': '#3d2314',
  'WARM SUNSET': '#c27a4a',
  'WAVE BLUE': '#1a4a4a',
  'SMOKE GREY': '#3e3e3e',
  'AMBER COPPER': '#b06030',
  // TRES finishes
  'BLACK BRONZE': '#5a4a2a',
  'METALLIC BLACK': '#1a1a1a',
  'VOLCANIC': '#a06858',
  'GOLDEN CAVA': '#b89840',
  '24K GOLD': '#ffd700',
  '24K MATTE GOLD': '#d4a830',
  '24K MATTE ROSE GOLD': '#dca888',
  'WALNUT LAMINATE': '#6b4c33',
  'OAK LAMINATE': '#b5a078',
};

export const getFinishStyle = (finish: string): FinishStyle => {
  const upperFinish = finish.toUpperCase().trim();
  
  // Check DB overrides first
  const dbMatch = findDbOverride(finish);
  if (dbMatch) return dbToFinishStyle(dbMatch);
  
  // Direct match
  if (finishStyles[upperFinish]) {
    return finishStyles[upperFinish];
  }
  
  const sortedKeys = Object.keys(finishStyles).sort((a, b) => b.length - a.length);
  
  for (const key of sortedKeys) {
    if (upperFinish.includes(key) || key.includes(upperFinish)) {
      return finishStyles[key];
    }
  }
  
  return {
    background: 'linear-gradient(145deg, #666666 0%, #555555 50%, #666666 100%)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.15), inset 0 -1px 2px rgba(0,0,0,0.3)',
  };
};

// Check if finish is dual-color (e.g., "BLACK/BRASS", "WHITE / STEEL")
export const isDualFinish = (finish: string): boolean => {
  return finish.includes('/');
};

// Get solid color for a single finish name
export const getFinishSolidColor = (finish: string): string => {
  const upperFinish = finish.toUpperCase().trim();
  
  // Check DB overrides first
  const dbMatch = findDbOverride(finish);
  if (dbMatch) return dbToSolidColor(dbMatch);
  
  if (finishSolidColors[upperFinish]) {
    return finishSolidColors[upperFinish];
  }
  
  const sortedKeys = Object.keys(finishSolidColors).sort((a, b) => b.length - a.length);
  
  for (const key of sortedKeys) {
    if (upperFinish.includes(key) || key.includes(upperFinish)) {
      return finishSolidColors[key];
    }
  }
  
  return '#666666';
};

// Get both colors for a dual finish
export const getDualFinishColors = (finish: string): [string, string] => {
  const parts = finish.split('/').map(p => p.trim());
  const color1 = getFinishSolidColor(parts[0] || '');
  const color2 = getFinishSolidColor(parts[1] || parts[0] || '');
  return [color1, color2];
};
